import { deepSignal } from 'deepsignal';
import { formatColorName, isYes } from '@util/common.js';
import CustomizeSection from '@components/overlays/shared/customize-section.jsx';
import Description from '@components/overlays/shared/description.jsx';
import ColorPicker from '@components/pickers/color-picker/color.picker.jsx';
import Picker from '@components/input/picker.input.jsx';
import LinkSetup from '@components/overlays/shared/link-setup.jsx';
import { CHARITY } from '@util/const.js';
import ScaledOverlayWrapper from '@components/overlays/wrapper/scaled-overlay-wrapper.jsx';
import {
  RewardsOverlayThemeOption,
  RewardsOverlayVisibilityTimerOption,
  RewardsOverlay,
  RewardsOverlayBackgroundColors,
  toRewardsOverlayBackgroundColor,
} from '@playlive/overlay-core/overlays/rewards';
import { rewards } from '@data/example/index.js';
import HorizontalCustomizeWrapper from '@components/overlays/wrapper/horizonal-customize-wrapper.jsx';
import YesNoButtonInput from '@components/input/yes-no-button.input.jsx';
import YesNoButtonInputWrapper from '@components/overlays/wrapper/yes-no-button-input-wrapper.jsx';
import useCommon from '@hooks/useCommon.js';

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  rewardsTheme: RewardsOverlayThemeOption.CLASSIC,
  rewardsBackgroundColor: 'default-red',
  highlightFeaturedRewards: 'yes',
  showQuantityClaimed: 'yes',
  showAllRewards: 'yes',
  rotationTimer: RewardsOverlayVisibilityTimerOption.ALWAYS,
});

export default function RewardsOverlayPage() {
  const { overlayInfo, overlayURL, overlayDemoURL, setConfigState } = useCommon(
    { config, forceTiltify: true }
  );

  const timerOptions = [
    {
      value: RewardsOverlayVisibilityTimerOption.FIVE_MINUTES,
      name: 'Five minutes',
    },
    {
      value: RewardsOverlayVisibilityTimerOption.TEN_MINUTES,
      name: 'Ten minutes',
    },
    {
      value: RewardsOverlayVisibilityTimerOption.FIFTEEN_MINUTES,
      name: 'Fifteen minutes',
    },
    {
      value: RewardsOverlayVisibilityTimerOption.ALWAYS,
      name: 'Always',
    },
  ];

  const themeOptions = [
    {
      name: 'Slanted',
      value: RewardsOverlayThemeOption.SLANTED,
    },
    {
      name: 'Rounded',
      value: RewardsOverlayThemeOption.ROUNDED,
    },
    {
      name: 'Classic',
      value: RewardsOverlayThemeOption.CLASSIC,
    },
  ];

  const rewardOptions = [
    {
      name: 'All rewards',
      value: 'yes',
    },
    {
      name: 'Highlighted rewards',
      value: 'no',
    },
  ];

  function filterColors(color) {
    return RewardsOverlayBackgroundColors.find((c) => c === color);
  }

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col xl:flex-row overflow-x-hidden">
        <div className="example_overlay overflow-x-hidden w-full min-h-72">
          <ScaledOverlayWrapper className="min-w-[570px]" allowScaling={false} defaultScale={'scale-100'}>
            <RewardsOverlay
              goalAmount={1000}
              currentAmount={750}
              showAllRewards={isYes(config.showAllRewards)}
              showQuantityClaimed={isYes(config.showQuantityClaimed)}
              highlightFeaturedRewards={isYes(config.highlightFeaturedRewards)}
              backgroundColor={toRewardsOverlayBackgroundColor(
                config.rewardsBackgroundColor
              )}
              demoMode
              theme={config.rewardsTheme}
              rewards={rewards}
              timer={config.rotationTimer}
            />
          </ScaledOverlayWrapper>
        </div>

        <HorizontalCustomizeWrapper>
          <CustomizeSection title="Choose Colors">
            <span className="label-text mb-2">
              Background color:{' '}
              <b>{formatColorName(config.rewardsBackgroundColor)}</b>
            </span>

            <ColorPicker
              fieldName="rewardsBackgroundColor"
              currentColor={config.rewardsBackgroundColor}
              filterFunction={filterColors}
              onChange={setConfigState}
            />
          </CustomizeSection>
          <CustomizeSection title="Theme">
            <div className="mt-5">
              <Picker
                items={themeOptions}
                fieldName="rewardsTheme"
                onChange={setConfigState}
                currentValue={config.rewardsTheme}
                labelText=""
              />
            </div>
          </CustomizeSection>

          <CustomizeSection title="Misc.">
            <div className="mt-5 mb-5">
              <Picker
                items={timerOptions}
                fieldName="rotationTimer"
                onChange={setConfigState}
                currentValue={config.rotationTimer}
                labelText="Set amount of time to rotate in and out or to always be visible
                on stream"
              />
            </div>
            <div className="mb-8">
              <Picker
                items={rewardOptions}
                fieldName="showAllRewards"
                onChange={setConfigState}
                currentValue={config.showAllRewards}
                labelText=" Display all or only highlighted rewards"
              />
            </div>

            <YesNoButtonInputWrapper>
              <YesNoButtonInput
                mode="checkbox"
                className="md:mr-4 mb-2 md:mb-0 flex-1"
                onChange={setConfigState}
                currentValue={config.highlightFeaturedRewards}
                name="highlightFeaturedRewards"
                label="Feature highlighted rewards"
              />

              <YesNoButtonInput
                className="flex-1"
                mode="checkbox"
                onChange={setConfigState}
                currentValue={config.showQuantityClaimed}
                name="showQuantityClaimed"
                label="Display quantity claimed"
              />
            </YesNoButtonInputWrapper>
          </CustomizeSection>

          {/* Charity Section Placeholder */}
        </HorizontalCustomizeWrapper>
      </div>

      <Description>
        <p className="pb-4">
          Enhance your Tiltify fundraising campaign with our dynamic Rewards
          overlay. Features include moving highlight rewards to the top,
          rotating in and out on a timer, automatic updates for new rewards, and
          customizable colors to fit your stream.
        </p>
      </Description>

      <LinkSetup
        constrainWidth={false}
        charityType={CHARITY.TILTIFY}
        obsDimensions={overlayInfo.obsDimensions}
        overlayName={overlayInfo.name}
        overlayURL={overlayURL}
        finalStepSlotPosition="middle"
        finalStepContent={
          <div className="mt-4">
            <a
              href={overlayDemoURL}
              rel="noopener noreferrer"
              className="link"
              target="_blank"
            >
              View in demo mode
            </a>
          </div>
        }
      />
    </div>
  );
}
